import type { ReactNode } from 'react';

import {
  parseInlineMarkdown,
  type InlineMarkdownToken,
} from '@/shared/utils/inline-markdown';

/**
 * The enrichment's inline markdown, as the reviewer should read it: `**bold**`
 * bolded, `[label](url)` shown as its label. Same parse as the customer-facing
 * deal-intelligence page, and deliberately the same two tokens — a reviewer
 * deciding whether a gap is fit to ship needs to see the text the customer
 * would see, not its source.
 *
 * A link renders as an inert `<span>` rather than an anchor or a button. On the
 * customer page a `[label](seenbefore:…)` opens the past-project modal; there is
 * no modal on this surface and nothing sensible to navigate to, so the label is
 * all there is to show. It takes the accent colour without an underline, which
 * is what the product page does for its own non-clickable link labels.
 */
function renderTokens(tokens: InlineMarkdownToken[]): ReactNode[] {
  return tokens.map((token, tokenIndex) => {
    if (token.type === 'text') return token.text;

    if (token.type === 'bold') {
      return (
        <strong className="gr-md-b" key={`bold-${tokenIndex}`}>
          {renderTokens(token.children)}
        </strong>
      );
    }

    return (
      <span className="gr-md-link" key={`link-${tokenIndex}`}>
        {token.label}
      </span>
    );
  });
}

export function InlineMarkdown({ text }: { text: string }) {
  return <>{renderTokens(parseInlineMarkdown(text))}</>;
}
